import type {
    ActiveDtc,
    BookedProduct,
    EngineOilState,
    SourceDevice,
    SourceType,
    Status,
} from '@/generated/GeneratedApiTypes';

/** Union of all value types of an object type. */
type ValueOf<T> = T[keyof T];

/**
 * Severity level for UI display (determines color).
 * Mirrors the `Status` x-extensible-enum from the Engine Oil API spec.
 */
export const STATUS = {
    DANGER: 'danger',
    WARNING: 'warning',
    NORMAL: 'normal',
    UNKNOWN: 'unknown',
} as const satisfies Record<string, NonNullable<Status>>;

export type StatusValue = ValueOf<typeof STATUS>;

/**
 * Descriptive state of the engine oil level.
 * Mirrors the `EngineOilState` x-extensible-enum from the Engine Oil API spec.
 */
export const ENGINE_OIL_STATE = {
    CRITICALLY_LOW: 'critically_low',
    LOW: 'low',
    OK: 'ok',
    TOO_HIGH: 'too_high',
    LEVEL_MAXIMUM: 'level_maximum',
    CRITICALLY_OVERFILLED: 'critically_overfilled',
    UNKNOWN: 'unknown',
} as const satisfies Record<string, NonNullable<EngineOilState>>;

export type EngineOilStateValue = ValueOf<typeof ENGINE_OIL_STATE>;

/**
 * The booked ServiceCare product level for the asset.
 * Mirrors the `BookedProduct` x-extensible-enum from the Engine Oil API spec.
 */
export const PRODUCT_LEVEL = {
    SERVICE_CARE_S: 'service_care_s',
    SERVICE_CARE_M: 'service_care_m',
    SERVICE_CARE_L: 'service_care_l',
} as const satisfies Record<string, NonNullable<BookedProduct>>;

export type ProductLevelValue = ValueOf<typeof PRODUCT_LEVEL>;

/**
 * The device type that produced the engine oil reading.
 * Mirrors the `SourceDevice` x-extensible-enum from the Engine Oil API spec.
 */
export const SOURCE_DEVICE = {
    CM4: 'cm4',
    TBM3: 'tbm3',
    OCU3: 'ocu3',
} as const satisfies Record<string, NonNullable<SourceDevice>>;

export type SourceDeviceValue = ValueOf<typeof SOURCE_DEVICE>;

/**
 * The source type of the engine oil reading.
 * Mirrors the `SourceType` x-extensible-enum from the Engine Oil API spec.
 */
export const SOURCE_TYPE = {
    SENSOR_SIGNAL_SINGLE: 'sensor_signal_single',
    SENSOR_SIGNAL_SERIES: 'sensor_signal_series',
    DID: 'did',
} as const satisfies Record<string, NonNullable<SourceType>>;

export type SourceTypeValue = ValueOf<typeof SOURCE_TYPE>;

/**
 * The type of an active engine oil DTC.
 * Mirrors the `ActiveDtc.type` x-extensible-enum from the Engine Oil API spec.
 */
export const ACTIVE_DTC_TYPE = {
    CRITICALLY_LOW: 'critically_low',
    CRITICALLY_OVERFILLED: 'critically_overfilled',
} as const satisfies Record<string, NonNullable<ActiveDtc>['type']>;

export type ActiveDtcTypeValue = ValueOf<typeof ACTIVE_DTC_TYPE>;

/**
 * Technical Information link for workshop engine oil actions.
 */
export const ENGINE_OIL_WORKSHOP_ACTIONS_TI_LINK =
    'https://secured.man.eu/vehicleexplorerui/?target=SI&man_language=en&nav=false';
export const ENGINE_OIL_WORKSHOP_ACTIONS_TI = 'TI: 7042';
